/**
 * État « En attente » des invitations à rejoindre la scène (présentateur ou intervenant), côté
 * régie. Rien n'est persisté en back : l'invitation vit dans un event Ably éphémère, l'acceptation
 * se lit dans les pistes LiveKit publiées par l'invité, le refus dans un event `*_JOIN_DECLINED`.
 * Un invité qui quitte la salle sans répondre (reload, onglet fermé) perd sa modale : on lève
 * l'attente dès qu'il disparaît de la salle, sinon le bouton resterait bloqué.
 */

export type InviteRequestEventKind = 'requested' | 'declined';

export interface InviteRequestEvent {
  kind: InviteRequestEventKind;
  identity: string;
}

export interface PendingInvite {
  /** L'invité a été vu dans la salle depuis la demande : sa disparition vaut abandon. */
  seenInRoom: boolean;
}

/** Objet plat plutôt qu'une Map : l'état vit dans le store Redux, il doit rester sérialisable. */
export type PendingInvites = Readonly<Record<string, PendingInvite>>;

export const EMPTY_PENDING_INVITES: PendingInvites = {};

const JOIN_EVENT_TYPES: Record<string, InviteRequestEventKind> = {
  PRESENTER_JOIN_REQUEST: 'requested',
  PRESENTER_JOIN_DECLINED: 'declined',
  SPEAKER_JOIN_REQUEST: 'requested',
  SPEAKER_JOIN_DECLINED: 'declined',
};

const readPayload = (payloadJson: unknown): Record<string, unknown> => {
  if (payloadJson && typeof payloadJson === 'object') {
    return payloadJson as Record<string, unknown>;
  }
  if (typeof payloadJson !== 'string') {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(payloadJson);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
};

const readIdentitySuffix = (value: unknown): string | null =>
  (typeof value === 'string' && value.length > 0) || typeof value === 'number'
    ? String(value)
    : null;

export const presenterIdentity = (presenterUserId: string | number): string =>
  `presenter-${presenterUserId}`;

export const speakerIdentity = (identitySuffix: string | number): string =>
  `speaker-${identitySuffix}`;

/** Lit un `live-event` Ably et le traduit en transition d'invitation, sinon `null`. */
export const readInviteRequestEvent = (data: unknown): InviteRequestEvent | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const { eventType, resourceType, payloadJson } = data as {
    eventType?: unknown;
    resourceType?: unknown;
    payloadJson?: unknown;
  };
  if (typeof eventType !== 'string') {
    return null;
  }
  const kind = JOIN_EVENT_TYPES[eventType];
  if (!kind) {
    return null;
  }
  const payload = readPayload(payloadJson);

  if (resourceType === 'PRESENTER') {
    const presenterUserId = readIdentitySuffix(payload.presenterUserId);
    return presenterUserId ? { kind, identity: presenterIdentity(presenterUserId) } : null;
  }
  if (resourceType === 'SPEAKER') {
    const speakerUserId = readIdentitySuffix(payload.speakerUserId);
    return speakerUserId ? { kind, identity: speakerIdentity(speakerUserId) } : null;
  }
  return null;
};

export const applyInviteRequestEvent = (
  pending: PendingInvites,
  event: InviteRequestEvent,
): PendingInvites => {
  const exists = Object.hasOwn(pending, event.identity);
  if (event.kind === 'requested') {
    return exists ? pending : { ...pending, [event.identity]: { seenInRoom: false } };
  }
  if (!exists) {
    return pending;
  }
  return Object.fromEntries(
    Object.entries(pending).filter(([identity]) => identity !== event.identity),
  );
};

export interface RoomSnapshot {
  /** Identités connectées à la salle, vues par le client LiveKit de la régie. */
  identitiesInRoom: ReadonlySet<string>;
  /** Identités connectées d'après l'API LiveKit (poll périodique), plus lente mais sans blip. */
  identitiesInRoomFromApi: ReadonlySet<string>;
  /** Identités qui publient des pistes (invitation acceptée et effective). */
  identitiesWithTracks: ReadonlySet<string>;
}

/**
 * Lève les attentes résolues par la salle : pistes publiées (acceptation) ou invité parti après
 * avoir été vu (abandon). L'abandon exige l'absence sur les deux sources : une reconnexion du
 * client LiveKit de la régie vide sa liste un instant, elle ne doit pas effacer les attentes.
 * Un invité pas encore vu est conservé : le client de la régie peut avoir un temps de retard sur
 * la liste API qui a activé le bouton.
 */
export const reconcilePendingInvites = (
  pending: PendingInvites,
  room: RoomSnapshot,
): PendingInvites => {
  let next: Record<string, PendingInvite> | null = null;

  for (const [identity, invite] of Object.entries(pending)) {
    const inRoom =
      room.identitiesInRoom.has(identity) || room.identitiesInRoomFromApi.has(identity);
    const resolved = room.identitiesWithTracks.has(identity) || (invite.seenInRoom && !inRoom);
    const seenInRoom = invite.seenInRoom || inRoom;

    if (!resolved && seenInRoom === invite.seenInRoom) {
      continue;
    }
    next ??= { ...pending };
    if (resolved) {
      delete next[identity];
    } else {
      next[identity] = { seenInRoom };
    }
  }

  return next ?? pending;
};
